import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../services/api';
import { audioEngine } from '../../services/audioEngine';
import { proxied } from '../../services/assets';
import { SLICE_DISTRICT } from '../../config';
import { useWorldStore, ZOOM } from '../../state/worldStore';
import { isTyping } from '../../utils/typing';
import { blip, whoosh } from '../sfx';
import { ensureHudFont, HUD_FONT } from './hudFont';

// ── THE RADIO WHEEL ──────────────────────────────────────────────────────────
//
// Hold Q, point at a station, let go. The most-loved control in the open-world
// genre, and on a platform whose world is MADE of radio stations it is the
// obvious one: every genre station HypeTunes runs, on one wheel, with its host.
//
// Retuning is per visitor and per session (audioEngine.switchStation) — the
// district's own station stays what the backend says (WORLD_STATION) for
// everyone else, and for you again after a reload. A slot for "radio off".
//
// Talk/podcast stations are left off the wheel: they have no rolling music
// feed, and a station that answers with silence reads as a broken wheel.

const OFF = { slug: '__off', name: 'Radio Off' };

function useStations() {
  const [list, setList] = useState(null);
  useEffect(() => {
    let dead = false;
    api.stations()
      .then((d) => {
        if (dead) return;
        const music = (d?.stations || []).filter((s) => s.is_active !== 0 && s.station_type !== 'podcast' && (s.track_count || 0) > 0);
        setList(music.slice(0, 11));
      })
      .catch(() => { if (!dead) setList([]); });
    return () => { dead = true; };
  }, []);
  return list;
}

export function StationCard({ station }) {
  if (!station) return null;
  return (
    <div key={station.at} style={{
      position: 'absolute', left: '50%', top: 'calc(max(16px, env(safe-area-inset-top)) + 52px)', transform: 'translateX(-50%)',
      textAlign: 'center', pointerEvents: 'none', animation: 'htStation 3200ms ease forwards',
    }}>
      <style>{`@keyframes htStation { 0% { opacity: 0; transform: translate(-50%, -6px); } 10% { opacity: 1; transform: translate(-50%, 0); } 80% { opacity: 1; } 100% { opacity: 0; } }`}</style>
      <div style={{ font: `30px/1 ${HUD_FONT}`, color: '#fff', textTransform: 'uppercase', letterSpacing: 1, textShadow: '2px 2px 0 #000, 0 4px 18px rgba(0,0,0,0.6)' }}>
        {station.name}
      </div>
      {station.host_name && (
        <div style={{ marginTop: 5, font: '700 12px system-ui, sans-serif', letterSpacing: 3, textTransform: 'uppercase', color: '#ffd27d', textShadow: '1px 1px 0 #000' }}>
          with {station.host_name}
        </div>
      )}
    </div>
  );
}

export default function RadioWheel({ onTuned }) {
  const stations = useStations();
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(-1);
  const zoom = useWorldStore((s) => s.zoom);
  const slots = useMemo(() => (stations ? [OFF, ...stations] : null), [stations]);
  const hoverRef = useRef(-1);
  hoverRef.current = hover;
  const openRef = useRef(false);
  openRef.current = open;

  useEffect(() => { ensureHudFont(); }, []);

  const current = () => (useWorldStore.getState().musicOn ? SLICE_DISTRICT.stationSlug : OFF.slug);

  const apply = async (slot) => {
    if (!slot) return;
    const st = useWorldStore.getState();
    if (slot.slug === OFF.slug) {
      if (st.musicOn) st.toggleMusic();
      await audioEngine.setMusicOn(false, { mine: true }).catch(() => {});
      onTuned?.({ name: 'Radio Off', at: Date.now() });
      return;
    }
    if (!st.musicOn) st.toggleMusic();
    await audioEngine.switchStation(slot.slug).catch(() => {});
    onTuned?.({ ...slot, at: Date.now() });
  };

  // Hold Q to open, release to tune. Only on the street, where the station is
  // the soundtrack (rooms have their own sound — RoomSound).
  useEffect(() => {
    if (zoom !== ZOOM.STREET) return undefined;
    const down = (e) => {
      if (e.code !== 'KeyQ' || e.repeat || isTyping(e) || !slots) return;
      // Q flies down in freecam/drone; leave it alone there.
      if (new URLSearchParams(window.location.search).get('freecam') === '1' || useWorldStore.getState().adminTool) return;
      e.preventDefault();
      const cur = current();
      setHover(Math.max(0, slots.findIndex((s) => s.slug === cur)));
      setOpen(true);
      whoosh(0.3);
    };
    const up = (e) => {
      if (e.code !== 'KeyQ' || !openRef.current) return;
      e.preventDefault();
      setOpen(false);
      const pick = slots?.[hoverRef.current];
      if (pick && pick.slug !== current()) { blip(990); apply(pick); }
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, [zoom, slots]); // eslint-disable-line react-hooks/exhaustive-deps

  // TOUCH: there is no Q to hold, so a button opens the wheel and it stays open
  // until a station (or the background) is tapped.
  const sticky = useRef(false);
  useEffect(() => {
    const openIt = () => {
      if (!slots) return;
      const cur = current();
      setHover(Math.max(0, slots.findIndex((x) => x.slug === cur)));
      sticky.current = true;
      setOpen(true);
    };
    window.addEventListener('ht:radio-open', openIt);
    return () => window.removeEventListener('ht:radio-open', openIt);
  }, [slots]); // eslint-disable-line react-hooks/exhaustive-deps

  // Point to choose: the slot is whichever way the mouse is from the centre.
  useEffect(() => {
    if (!open || !slots) return undefined;
    const move = (e) => {
      const dx = e.clientX - window.innerWidth / 2; const dy = e.clientY - window.innerHeight / 2;
      if (Math.hypot(dx, dy) < 40) return;
      const a = (Math.atan2(dy, dx) + Math.PI / 2 + Math.PI * 2) % (Math.PI * 2);   // 0 at the top, clockwise
      const i = Math.round(a / ((Math.PI * 2) / slots.length)) % slots.length;
      if (i !== hoverRef.current) { setHover(i); blip(520 + i * 40, 0.4); }
    };
    window.addEventListener('pointermove', move);
    return () => window.removeEventListener('pointermove', move);
  }, [open, slots]);

  if (!open || !slots) return null;
  const n = slots.length;
  const R = Math.min(230, Math.min(window.innerWidth, window.innerHeight) * 0.34);
  const sel = slots[hover] || null;
  return (
    <div
      onClick={(e) => { if (sticky.current && e.target === e.currentTarget) { sticky.current = false; setOpen(false); } }}
      style={{
        position: 'fixed', inset: 0, zIndex: 60, pointerEvents: 'auto',
        background: 'radial-gradient(circle at center, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.62) 70%)',
      }}
    >
      <div style={{ position: 'absolute', left: '50%', top: '50%', width: 0, height: 0 }}>
        <div style={{
          position: 'absolute', left: -R - 52, top: -R - 52, width: (R + 52) * 2, height: (R + 52) * 2, borderRadius: '50%',
          border: '2px solid rgba(255,255,255,0.08)', background: 'rgba(8,10,14,0.35)', backdropFilter: 'blur(3px)',
        }} />
        {slots.map((s, i) => {
          const a = (i / n) * Math.PI * 2 - Math.PI / 2;
          const x = Math.cos(a) * R; const y = Math.sin(a) * R;
          const on = i === hover;
          const img = s.cover_image_url ? proxied(s.cover_image_url) : null;
          return (
            <button
              key={s.slug}
              onClick={() => { sticky.current = false; setOpen(false); if (s.slug !== current()) apply(s); }}
              onPointerEnter={() => setHover(i)}
              style={{
                position: 'absolute', left: x - 44, top: y - 44, width: 88, height: 88, borderRadius: '50%', padding: 0,
                border: on ? '3px solid #ffd27d' : '2px solid rgba(255,255,255,0.25)', cursor: 'pointer', overflow: 'hidden',
                transform: `scale(${on ? 1.18 : 1})`, transition: 'transform 120ms ease, border-color 120ms ease',
                boxShadow: on ? '0 0 0 6px rgba(255,210,125,0.18), 0 10px 30px rgba(0,0,0,0.6)' : '0 6px 18px rgba(0,0,0,0.5)',
                background: img ? `center / cover no-repeat url("${img}")` : 'radial-gradient(circle, #2a2e36, #111317)',
                display: 'grid', placeItems: 'center', color: '#fff',
              }}
              aria-label={s.name}
            >
              {!img && <span style={{ font: '800 12px system-ui', letterSpacing: 1.5, textTransform: 'uppercase', opacity: 0.85 }}>{s.slug === OFF.slug ? 'Off' : s.name}</span>}
            </button>
          );
        })}
        {sel && (
          <div style={{ position: 'absolute', left: -150, top: -60, width: 300, height: 120, display: 'grid', placeItems: 'center', textAlign: 'center', pointerEvents: 'none' }}>
            <div>
              <div style={{ font: `30px/1.05 ${HUD_FONT}`, color: '#fff', textTransform: 'uppercase', textShadow: '2px 2px 0 #000' }}>{sel.name}</div>
              {sel.host_name && <div style={{ marginTop: 6, font: '700 11px system-ui', letterSpacing: 3, color: '#ffd27d', textTransform: 'uppercase' }}>hosted by {sel.host_name}</div>}
              {sel.track_count ? <div style={{ marginTop: 4, font: '600 11px system-ui', color: 'rgba(255,255,255,0.6)' }}>{sel.track_count} tracks</div> : null}
            </div>
          </div>
        )}
      </div>
      <div style={{ position: 'absolute', left: 0, right: 0, top: `calc(50% + ${R + 74}px)`, textAlign: 'center', font: '600 12px system-ui', color: 'rgba(255,255,255,0.7)', letterSpacing: 1 }}>
        {sticky.current ? 'Tap a station' : 'Point to a station · release Q to tune'}
      </div>
    </div>
  );
}
